import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStreakEmoji } from '../utils/streaks';

export default function HabitCard({ habit, isCheckedIn, onCheckin, onUndo }) {
  const navigate = useNavigate();
  const [animating, setAnimating] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleTap(e) {
    e.stopPropagation();
    if (busy) return; // guard against rapid double-taps firing two mutations
    setBusy(true);
    setAnimating(true);

    try {
      if (isCheckedIn) {
        await onUndo(habit.habitId);
      } else {
        await onCheckin(habit.habitId);
      }
    } catch {
      // handled by store
    } finally {
      setBusy(false);
    }

    setTimeout(() => setAnimating(false), 300);
  }

  function openDetail() {
    navigate(`/habit/${habit.habitId}`);
  }

  const streakEmoji = getStreakEmoji(habit.currentStreak || 0);

  return (
    <div
      onClick={openDetail}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(); }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Open ${habit.name}`}
      className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
          style={{ backgroundColor: `${habit.color}20` }}
        >
          {habit.icon}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">
            {habit.name}
          </h3>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            {habit.currentStreak > 0 && (
              <>
                <span style={{ color: habit.color }} className="font-medium">
                  {habit.currentStreak} day{habit.currentStreak !== 1 ? 's' : ''}
                </span>
                {streakEmoji && <span>{streakEmoji}</span>}
              </>
            )}
            {habit.currentStreak === 0 && <span>Start your streak!</span>}
          </div>
        </div>

        {/* Check-in button */}
        <button
          type="button"
          onClick={handleTap}
          disabled={busy}
          aria-pressed={isCheckedIn}
          aria-busy={busy}
          aria-label={`${isCheckedIn ? 'Undo check-in for' : 'Check in'} ${habit.name}`}
          className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-60 ${
            animating ? 'animate-scale-in' : ''
          } ${
            isCheckedIn
              ? 'text-white shadow-lg'
              : 'border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }`}
          style={
            isCheckedIn
              ? { backgroundColor: habit.color }
              : {}
          }
        >
          {isCheckedIn ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: habit.color, opacity: 0.4 }}
            />
          )}
        </button>
      </div>
    </div>
  );
}
